import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import Icon from "@client/controls/Icon/component";
import { Loading } from "@client/controls/Loading/component";
import { Activity, type ActivityItem } from "@client/controls/Timeline/component";
import { sort } from "@client/lib/array";
import { formatToHtml } from "@client/lib/format";
import { getUserDisplayName } from "@client/lib/misc";
import { IconType } from "@client/types";
import { pickUserProfilePictureProps, UserProfilePicture } from "@common/components/ProfilePicture/component";
import { useI18n } from "@common/hooks/i18n";
import { i18nFormat } from "@common/lib/i18n";
import React from "react";
import { Approved, QuoteComment } from "../../Components";
import { getHtmlId } from "../../lib";
import { type Forums } from "../../types";
import Actions from "../Actions";

export type MapProps = RootContainerProps & {
  apiInfo: ModuleApiInfo | null;
  comments: Forums.PostComment[];
  isLoading: boolean;
  lastAddedCommentId: string | null;
  userId?: number | null;
};

export type ActionProps = {
  onDeleteClick: (ticketCommentId: string) => void;
  onReplyClick: (ticketCommentId: string) => void;
  onUpdateClick: (ticketCommentId: string) => void;
};

const Comments = (props: ActionProps & MapProps) => {
  const i18n = useI18n();

  const items = React.useMemo((): ActivityItem[] => {
    return sort(props.comments, { key: "createdOnDate", type: "date", order: "asc" }).map((x) => ({
      date: x.createdOnDate,
      description: (
        <div>
          {!!x.parentTicketCommentId && <QuoteComment comment={x} isParent />}

          <div className="comment" dangerouslySetInnerHTML={{ __html: formatToHtml(x.comment) }}></div>

          <div className="actions_wrap">
            {!!props.userId && (
              <div className="item reply" onClick={() => props.onReplyClick(x.ticketCommentId)}>
                <Icon iconClassName={IconType.Comment} /> {i18n.reply}
              </div>
            )}
            {x.createdByUserId === props.userId && (
              <Actions
                onDeleteClick={() => props.onDeleteClick(x.ticketCommentId)}
                onUpdateClick={() => props.onUpdateClick(x.ticketCommentId)}
              />
            )}
            {x.createdByUserId === props.userId && <Approved isApproved={x.isApproved} />}
          </div>
        </div>
      ),
      icon: <UserProfilePicture {...pickUserProfilePictureProps(x)} size={36} />,
      id: getHtmlId(x.ticketCommentId),
      title: getUserDisplayName({
        firstName: x.userFirstName,
        lastName: x.userLastName
      })
    }));
  }, [props.comments, props.onDeleteClick, props.onReplyClick, props.onUpdateClick, props.userId]);

  React.useEffect(() => {
    if (props.lastAddedCommentId) {
      const el = document.querySelector(`#${getHtmlId(props.lastAddedCommentId)}`);
      el && el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [props.lastAddedCommentId]);

  if (props.isLoading) return <Loading />;
  if (!items.length) return null;

  return (
    <div className="post_comments">
      <p>{i18nFormat(i18n.commentsN, ["N", items.length])}</p>
      <Activity isBorderless isNarrow options={items} />
    </div>
  );
};
export default Comments;
